<template>
  <p class="text-lg font-semibold text-black">
    Check Your Email!
  </p>
  <p class="text-center text-[--color-neutral-neutral]">
    We've sent a one-time login link to <br>
    <span class="text-[--color-neutral-black]">{{ email }}</span>
  </p>
  <Button
    v-if="!showCode"
    :loading="loading"
    label="Enter code manually"
    class="my-2 w-full"
    size="large"
    label-class="font-medium text-base"
    severity="tertiary"
    @click="showCode = true"
  />
  <div v-if="showCode" class="flex w-full flex-col gap-4">
    <div class="flex flex-col gap-2">
      <InputText
        v-model="code"
        placeholder="Enter code"
        class="!h-[48px] w-full"
        :disabled="loading"
        :invalid="isError"
        @keydown.enter="verifyCode"
        @input="handleInputToUppercase"
      />
      <small
        class="hidden text-[--v3-input-field-inputerrorbordercolor]"
        :class="{
          '!block': isError,
        }"
      >Please enter a valid verification code.</small>
    </div>
    <Button
      :loading="loading"
      :disabled="!code || !email"
      :label="label"
      class="w-full"
      size="large"
      label-class="font-medium text-base"
      @click="verifyCode"
    />
  </div>
  <p v-if="!isResent" class="text-center text-[--color-neutral-neutral]">
    Didn't get the email?<br> Check spam or <button class="text-[--v3-theme-primarycolor] underline" @click="resendCode">
      Click to resend
    </button>
  </p>
  <div v-else>
    <p class="text-regular-medium mb-4 text-center">
      Still no email?
    </p>
    <p v-if="route.name === 'login.confirm'" class="leading-8 text-[--color-neutral-neutral]">
      1. Make sure your address is correct.<br>
      2. Still no luck? <a class="font-medium text-[--v3-theme-primarycolor]" href="/contact">Contact support</a>
    </p>
    <p v-else class="leading-8 text-[--color-neutral-neutral]">
      1. Make sure your address is correct.<br>
      2. Try a different email.<br>
      3. Still no luck? <a class="font-medium text-[--v3-theme-primarycolor]" href="/contact">Contact support</a>
    </p>
  </div>
  <RouterLink v-if="route.name === 'registration.confirm'" :to="{name: 'registration.entry'}" class="flex items-center gap-2 text-center text-[--color-neutral-neutral-light]">
    <Icon name="arrow-back" class="size-[18px]" /> Back to sign up
  </RouterLink>
  <RouterLink
    v-else
    :to="{name: 'login.entry'}"
    class="flex items-center gap-2 text-center text-[--color-neutral-neutral-light]"
    @click="trackSignIn"
  >
    <Icon name="arrow-back" class="size-[18px]" /> Back to sign in
  </RouterLink>
</template>
  
<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import InputText from 'primevue/inputtext';
import { computed, onMounted, ref, watch } from 'vue';
import { useChallengeV3, useRecaptchaProvider } from 'vue-recaptcha';
import { useRoute, useRouter } from 'vue-router';

import Button from '@/common/components/atoms/Button';
import Icon from '@/common/components/atoms/Icon';
import { mixPanelEvents } from '@/common/constants';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { track } from '@/helpers/analytics/mixpanel';
import { axios, sanctumCookie } from '@/helpers/axios';
import {
  leaveAuthBreadcrumb,
  notifyAuthError,
  sanitizeEmail,
} from '@/helpers/bugsnag';
import { isRecaptchaBypassEmail } from '@/helpers/recaptchaBypass';
import { useAuthStore } from '@/modules/auth/stores/auth';

useRecaptchaProvider();

const authStore = useAuthStore();
const { email } = storeToRefs(authStore);
const route = useRoute();
const router = useRouter();
const toast = useTrackedToast();
const showCode = ref(false);
const code = ref(null);
const loading = ref(false);
const isError = ref(false);
const isResent = ref(false);
const label = computed(() => (loading.value ? 'Verifying code...' : 'Continue with Login Code'));
const { execute } = useChallengeV3('submit');
const sanitizedEmail = computed(() => sanitizeEmail(email.value));

onMounted(() => {
  leaveAuthBreadcrumb('Magic link confirmation page mounted', {
    email: sanitizedEmail.value,
    routeName: route.name,
  });

  if (route.name === 'registration.confirm' && !email.value) {
    leaveAuthBreadcrumb('Registration confirmation page missing email; redirecting to registration', {
      routeName: route.name,
    });
    router.replace({ name: 'registration.entry' });
  }
});

const verifyCode = async () => {
  if (!email.value) {
    leaveAuthBreadcrumb('Verification code submission blocked because email is missing', {
      stage: 'verify-code',
      routeName: route.name,
    });
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please return and enter your email address again.',
      life: 3000,
      errorCode: 'EMAIL_REQUIRED',
    });
    return;
  }

  loading.value = true;
  const verificationEmail = email.value;
  leaveAuthBreadcrumb('Attempting verification code submission', {
    codeLength: String(code.value ?? '').length,
    email: sanitizedEmail.value,
    routeName: route.name,
  });
  track('Viewed Magic Link Confirmation Page', {
    'Used Manual Code Entry': true,
    'Resent Magic Link': false,
    'Back to Sign In': false,
    'Authentication error': '',
    'Erank Version': 3,
  });
  
  try {
    await sanctumCookie();
    const response = await axios.post('/auth/verify-code', {
      email: verificationEmail,
      code: code.value,
    });
    leaveAuthBreadcrumb('Verification code accepted', {
      email: sanitizedEmail.value,
    });

    if (route.name === 'login.confirm') {
      track(mixPanelEvents.LOGGED_IN, {
        'Erank Version': 3,
        'Login Method': 'Magic Link',
        'First Time': response.data.last_login ? 'False' : 'True',
      });
      let location = route.query.location || 'dashboard';
      let path = typeof location === 'string' && location.startsWith('/') ? location : '/' + location;
      window.location.href = path;
    } else {
      leaveAuthBreadcrumb('Registration flow continuing after code verification', {
        email: sanitizedEmail.value,
      });
      // Trigger GTM sign-up event for Magic Link registration
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'user_signup', // GTM listens for this
        email: verificationEmail,
      });
      
      // Track referral with FirstPromoter
      if (window.fpr && verificationEmail) {
        window.fpr("referral", { email: verificationEmail });
      }

      window.location.href = '/onboarding/newsletter?registered_via_magiclink=1';
    }
  } catch (error) {
    isError.value = true;
    notifyAuthError(error, {
      email: sanitizedEmail.value,
      stage: 'verify-code',
      status: error.response?.status,
    });

    track('Received Error on Registration', {
      'Source': 'Registration',
      'Error Message': error.response?.data ?? error.message,
      'Erank Version': 3,
    });

    track('Viewed Magic Link Confirmation Page', {
      'Used Manual Code Entry': false,
      'Resent Magic Link': false,
      'Back to Sign In': false,
      'Authentication error': error.response?.data ?? error.message,
      'Erank Version': 3,
    });
    loading.value = false;
  }
};

const resendCode = async () => {
  if (!email.value) {
    leaveAuthBreadcrumb('Resend magic link blocked because email is missing', {
      stage: 'resend-code',
      routeName: route.name,
    });
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please return and enter your email address again.',
      life: 3000,
      errorCode: 'EMAIL_REQUIRED',
    });
    return;
  }

  loading.value = true;
  let url = '/auth/register';

  if (route.name === 'login.confirm') {
    url = '/auth/email';
  }

  track('Viewed Magic Link Confirmation Page', {
    'Used Manual Code Entry': false,
    'Resent Magic Link': true,
    'Back to Sign In': false,
    'Authentication error': '',
    'Erank Version': 3,
  });

  leaveAuthBreadcrumb('Resend magic link requested', {
    email: sanitizedEmail.value,
    routeName: route.name,
  });

  try {
    const isProductionBuild = import.meta.env.VITE_APP_ENV === 'production';
    const needsRecaptcha = isProductionBuild && !isRecaptchaBypassEmail(email.value ?? '');
    let gRecaptchaResponse = '';

    if (needsRecaptcha) {
      leaveAuthBreadcrumb('Executing resend reCAPTCHA', {
        email: sanitizedEmail.value,
      });
      gRecaptchaResponse = await execute();
    }

    if (needsRecaptcha && !gRecaptchaResponse) {
      notifyAuthError(new Error('Resend reCAPTCHA token missing'), {
        email: sanitizedEmail.value,
      });
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'reCAPTCHA verification failed. Please try again.',
        life: 3000,
        errorCode: 'RECAPTCHA_VERIFICATION_FAILED',
      });
      loading.value = false;
      return;
    }

    const payload = {
      email: email.value, 
      resend: true,
    };
    if (needsRecaptcha) {
      payload['g-recaptcha-response'] = gRecaptchaResponse;
    }
    await sanctumCookie();
    await axios.post(url, payload);
    leaveAuthBreadcrumb('Resent magic link request succeeded', {
      email: sanitizedEmail.value,
      routeName: route.name,
    });
    isResent.value = true;
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Resent successfully',
      life: 3000,
    });
  } catch (error) {
    notifyAuthError(error, {
      email: sanitizedEmail.value,
      stage: 'resend-code',
      status: error.response?.status,
    });
    track('Received Error on Registration', {
      'Source': 'Registration',
      'Error Message': error.response?.data ?? error.message,
      'Erank Version': 3,
    });
    
    const statusCode = error.response?.status ? `${error.response.status}` : '';
    const errorCode = statusCode ? `${statusCode} REGISTRATION_FAILED` : 'REGISTRATION_FAILED';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data ?? error.message,
      life: 3000,
      errorCode,
    });
  } finally {
    loading.value = false;
  }
};

const trackSignIn = () => {
  leaveAuthBreadcrumb('Returning to sign in from confirmation page', {
    email: sanitizedEmail.value,
    routeName: route.name,
  });
  track('Viewed Magic Link Confirmation Page', {
    'Used Manual Code Entry': false,
    'Resent Magic Link': false,
    'Back to Sign In': true,
    'Authentication error': '',
    'Erank Version': 3,
  });
};

const handleInputToUppercase = (event) => {
  // Update the data property with the uppercase version of the input value
  code.value = event.target.value.toUpperCase();
};

watch(code, () => {
  isError.value = false;
});
</script>
  
